import { useCallback, useEffect, useRef, useState } from 'react'

import { saveCanvasGraph, type CanvasGraphScope } from '@/api/studio-production'
import { saveCreativeGraph } from '@/api/studio-creative'
import type { StudioFlowEdge, StudioFlowNode } from '@/features/studio/canvas/types'
import { isCanvasGraphConflict, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'

export type CanvasSaveStatus = 'saved' | 'saving' | 'dirty' | 'error' | 'conflict'

type GraphSnapshot = {
  nodes: StudioFlowNode[]
  edges: StudioFlowEdge[]
  viewport: { x: number; y: number; zoom: number }
}

export function useCanvasGraphSave(opts: {
  enabled: boolean
  graphMode?: 'production' | 'creative'
  productionId: string
  canvasScope: CanvasGraphScope
  getSnapshot: () => GraphSnapshot
  stripNode: (node: StudioFlowNode) => StudioFlowNode
  delayMs?: number
  onSaveError?: (message: string) => void
  onSaveSuccess?: () => void
}) {
  const { enabled, graphMode = 'production', productionId, canvasScope, getSnapshot, stripNode, delayMs = 2000, onSaveError, onSaveSuccess } = opts

  const [status, setStatus] = useState<CanvasSaveStatus>('saved')
  const revisionRef = useRef<number | undefined>(undefined)
  const dirtyRef = useRef(false)
  const conflictRef = useRef(false)
  const resavePendingRef = useRef(false)
  const savingRef = useRef(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flushPromiseRef = useRef<Promise<boolean> | null>(null)

  const setRevision = useCallback((revision?: number) => {
    revisionRef.current = revision
  }, [])

  const clearConflict = useCallback(() => {
    conflictRef.current = false
  }, [])

  const resetClean = useCallback(() => {
    dirtyRef.current = false
    resavePendingRef.current = false
    conflictRef.current = false
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    setStatus('saved')
  }, [])

  const persistSnapshot = useCallback(
    async (opts?: { skipRevisionCheck?: boolean }): Promise<boolean> => {
      const snap = getSnapshot()
      const payload = {
        nodes: snap.nodes.map(stripNode),
        edges: snap.edges,
        viewport: snap.viewport,
        ...(opts?.skipRevisionCheck ? {} : { if_match_revision: revisionRef.current }),
      }
      const res =
        graphMode === 'creative'
          ? await saveCreativeGraph(productionId, payload)
          : await saveCanvasGraph(productionId, payload, canvasScope)
      revisionRef.current = res.revision
      dirtyRef.current = false
      conflictRef.current = false
      setStatus('saved')
      onSaveSuccess?.()
      return true
    },
    [graphMode, productionId, canvasScope, getSnapshot, stripNode, onSaveSuccess],
  )

  const runSave = useCallback(async (): Promise<boolean> => {
    if (!enabled) return true
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    if (savingRef.current) {
      resavePendingRef.current = true
      return false
    }
    if (!dirtyRef.current) {
      setStatus('saved')
      return true
    }

    savingRef.current = true
    setStatus('saving')
    try {
      return await persistSnapshot()
    } catch (e) {
      if (isCanvasGraphConflict(e)) {
        conflictRef.current = true
        setStatus('conflict')
        onSaveError?.(resolveApiErrorMessage(e, 'canvas.errors.conflict'))
        return false
      }
      onSaveError?.(resolveApiErrorMessage(e, 'canvas.errors.saveFailed'))
      setStatus('error')
      return false
    } finally {
      savingRef.current = false
      if (resavePendingRef.current && !conflictRef.current) {
        resavePendingRef.current = false
        void runSave()
      }
    }
  }, [enabled, persistSnapshot, onSaveError])

  const markDirty = useCallback(() => {
    if (!enabled) return
    dirtyRef.current = true
    if (conflictRef.current) {
      setStatus('conflict')
      return
    }
    setStatus((prev) => (prev === 'saving' ? 'saving' : 'dirty'))
    if (savingRef.current) {
      resavePendingRef.current = true
      return
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      void runSave()
    }, delayMs)
  }, [enabled, delayMs, runSave])

  const flushSave = useCallback(async (): Promise<boolean> => {
    if (!enabled) return true
    if (conflictRef.current) return false
    dirtyRef.current = true
    if (flushPromiseRef.current) return flushPromiseRef.current
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    const task = runSave()
    flushPromiseRef.current = task
    try {
      return await task
    } finally {
      if (flushPromiseRef.current === task) {
        flushPromiseRef.current = null
      }
    }
  }, [enabled, runSave])

  const forceSave = useCallback(async (): Promise<boolean> => {
    if (!enabled) return true
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    if (savingRef.current) {
      resavePendingRef.current = true
      return false
    }
    dirtyRef.current = true
    savingRef.current = true
    setStatus('saving')
    try {
      return await persistSnapshot({ skipRevisionCheck: true })
    } catch (e) {
      onSaveError?.(resolveApiErrorMessage(e, 'canvas.errors.saveFailed'))
      setStatus('error')
      return false
    } finally {
      savingRef.current = false
      if (resavePendingRef.current && !conflictRef.current) {
        resavePendingRef.current = false
        void runSave()
      }
    }
  }, [enabled, persistSnapshot, onSaveError, runSave])

  const hasPendingChanges = useCallback(() => {
    return dirtyRef.current || savingRef.current || resavePendingRef.current
  }, [])

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!hasPendingChanges()) return
      e.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasPendingChanges])

  return {
    status,
    markDirty,
    flushSave,
    forceSave,
    resetClean,
    clearConflict,
    setRevision,
    hasPendingChanges,
  }
}
